import React, { useState, useEffect, useRef } from 'react';
import { Button, Heading, Stack, Box, InputGroup, Input, InputLeftElement, IconButton, Text, Spinner, VStack, HStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  calcularFechaFin,
  evaluarDisponibilidad,
} from '../../utils/reservaDisponibilidad';

const CalendarSvg = ({ boxSize = 48 }) => (
  <svg
    width={boxSize}
    height={boxSize}
    viewBox="0 0 48 48"
    fill="none"
    style={{ display: 'inline-block' }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="6" y="10" width="36" height="32" rx="4" fill="#319795"/>
    <rect x="6" y="10" width="36" height="8" fill="#2C7A7B"/>
    <rect x="14" y="6" width="4" height="8" rx="2" fill="#fff"/>
    <rect x="30" y="6" width="4" height="8" rx="2" fill="#fff"/>
    <rect x="14" y="22" width="4" height="4" rx="2" fill="#fff"/>
    <rect x="22" y="22" width="4" height="4" rx="2" fill="#fff"/>
    <rect x="30" y="22" width="4" height="4" rx="2" fill="#fff"/>
    <rect x="14" y="30" width="4" height="4" rx="2" fill="#fff"/>
    <rect x="22" y="30" width="4" height="4" rx="2" fill="#fff"/>
    <rect x="30" y="30" width="4" height="4" rx="2" fill="#fff"/>
  </svg>
);

const FaseFechaHora = ({ onNext, onBack, tipoServicio }) => {
  const [fechaHora, setFechaHora] = useState('');
  const [error, setError] = useState('');
  const [estadoDisponibilidad, setEstadoDisponibilidad] = useState('pendiente'); // 'pendiente', 'verificando', 'disponible', 'conflicto', 'error'
  const [horaPropuesta, setHoraPropuesta] = useState(null);
  const [tipoConflicto, setTipoConflicto] = useState(null);
  const [, setDiasInhabiles] = useState(null);
  const [, setReservasActivas] = useState([]);
  const [margenDespues, setMargenDespues] = useState(null); // null hasta que se cargue
  const [margenAntes, setMargenAntes] = useState(null); // null hasta que se cargue
  const [margenServicioProgramado, setMargenServicioProgramado] = useState(12); // horas mínimas de antelación
  const [configCargada, setConfigCargada] = useState(false); // Para saber si ya se cargaron las configs
  const inputRef = useRef();
  const { t } = useTranslation();
  const { accessToken } = useAuth();

  // Cargar configuraciones de márgenes al montar el componente
  useEffect(() => {
    const cargarConfiguraciones = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        
        let valorDespuesObtenido = 30; // Valor por defecto
        let valorAntesObtenido = 0; // Valor por defecto
        let valorMargenServicioProgramado = 12; // Valor por defecto en horas
        
        // Cargar antelación mínima de servicio programado
        try {
          const urlAntelacionServicio = `${apiBaseUrl}/api/settings/public/antelacion-minima-servicio-programado`;
          const responseAntelacion = await fetch(urlAntelacionServicio);
          if (responseAntelacion.ok) {
            const dataAntelacion = await responseAntelacion.json();
            if (typeof dataAntelacion.antelacion_horas === 'number') {
              valorMargenServicioProgramado = dataAntelacion.antelacion_horas;
              console.log('[FaseFechaHora] ✅ Antelación mínima servicio programado cargado:', valorMargenServicioProgramado, 'horas');
            }
          }
        } catch (error) {
          console.warn('[FaseFechaHora] ⚠️ Error al cargar antelación mínima servicio programado, usando valor por defecto:', error);
        }
        
        // Cargar márgenes desde el endpoint público
        try {
          const urlMargenes = `${apiBaseUrl}/api/settings/public/margenes_disponibilidad`;
          console.log('[FaseFechaHora] 📡 Solicitando márgenes desde:', urlMargenes);
          const response = await fetch(urlMargenes);
          console.log('[FaseFechaHora] 📥 Response status márgenes:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('[FaseFechaHora] 📦 Datos recibidos:', data);
            
            if (typeof data.margen_despues === 'number') {
              valorDespuesObtenido = data.margen_despues;
              console.log('[FaseFechaHora] ✅ Margen después cargado:', valorDespuesObtenido);
            }
            
            if (typeof data.margen_antes === 'number') {
              valorAntesObtenido = data.margen_antes;
              console.log('[FaseFechaHora] ✅ Margen antes cargado:', valorAntesObtenido);
            }
          } else {
            const errorText = await response.text();
            console.warn('[FaseFechaHora] ⚠️ Error HTTP al cargar márgenes:', response.status, errorText);
          }
        } catch (error) {
          console.warn('[FaseFechaHora] ⚠️ Error al cargar márgenes, usando valores por defecto:', error);
        }
        
        // Establecer ambos valores a la vez
        setMargenDespues(valorDespuesObtenido);
        setMargenAntes(valorAntesObtenido);
        setMargenServicioProgramado(valorMargenServicioProgramado);
        setConfigCargada(true);
        
        console.log(`[FaseFechaHora] 🎯 Configuración final: margenAntes=${valorAntesObtenido}min, margenDespues=${valorDespuesObtenido}min, margenServicioProgramado=${valorMargenServicioProgramado}h`);
      } catch (error) {
        console.error('[FaseFechaHora] ❌ Error al cargar configuraciones:', error);
        // Establecer valores por defecto en caso de error
        setMargenDespues(30);
        setMargenAntes(0);
        setConfigCargada(true);
      }
    };
    
    cargarConfiguraciones();
  }, []);

  // Verificar disponibilidad cuando cambia la fecha/hora
  useEffect(() => {
    const verificarDisponibilidad = async () => {
      if (!fechaHora) {
        setEstadoDisponibilidad('pendiente');
        return;
      }

      // Esperar a que se carguen las configuraciones
      if (!configCargada || margenDespues === null || margenAntes === null) {
        console.log('[FaseFechaHora] ⏳ Esperando configuración de márgenes...');
        return;
      }

      console.log(`[FaseFechaHora] 🔍 Iniciando verificación con márgenes: antes=${margenAntes}min, después=${margenDespues}min, servicioProgramado=${margenServicioProgramado}h`);

      // Validar que la fecha cumple con el mínimo configurado de horas
      const minDate = new Date(Date.now() + margenServicioProgramado * 60 * 60 * 1000);
      const fechaSeleccionada = new Date(fechaHora);
      
      if (fechaSeleccionada < minDate) {
        setError(t('fecha_hora_error_minimo', { horas: margenServicioProgramado }));
        setEstadoDisponibilidad('pendiente');
        return;
      }

      setError('');
      setEstadoDisponibilidad('verificando');

      try {
        const inicioReserva = new Date(fechaHora);
        const finReserva = calcularFechaFin(inicioReserva);

        if (isNaN(inicioReserva) || isNaN(finReserva)) {
          console.error("[FaseFechaHora] Fechas no válidas");
          setEstadoDisponibilidad('error');
          return;
        }

        const mes = inicioReserva.getMonth() + 1;
        const anio = inicioReserva.getFullYear();
        const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

        // Obtener períodos inhábiles
        let datosInhabiles = null;
        try {
          const responseInhabiles = await fetch(`${apiUrl}/api/mes_inhabil/${mes}/${anio}/`);
          if (responseInhabiles.ok) {
            datosInhabiles = await responseInhabiles.json();
            setDiasInhabiles(datosInhabiles);
          }
        } catch (error) {
          console.warn("[FaseFechaHora] No se pudieron obtener periodos inhábiles:", error);
        }

        // Obtener reservas activas
        let reservasActivasList = [];
        try {
          const headers = {
            'Content-Type': 'application/json',
          };
          if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
            console.log("[FaseFechaHora] Usando token:", accessToken.substring(0, 20) + "...");
          } else {
            console.warn("[FaseFechaHora] No hay accessToken disponible");
          }
          
          const responseReservas = await fetch(`${apiUrl}/api/reservas/`, {
            headers: headers,
            credentials: 'include' // Incluir cookies de sesión
          });
          if (responseReservas.ok) {
            const todasReservas = await responseReservas.json();
            reservasActivasList = todasReservas.filter(r => 
              r.estado === 'activo' && 
              r.fecha_hora_inicio && 
              r.fecha_hora_fin
            );
            setReservasActivas(reservasActivasList);
            console.log(`[FaseFechaHora] ${reservasActivasList.length} reservas activas obtenidas`);
          } else if (responseReservas.status === 401) {
            console.warn("[FaseFechaHora] Error 401. El token puede haber expirado.");
          }
        } catch (error) {
          console.warn("[FaseFechaHora] Error al obtener reservas:", error);
          // Continuar sin verificar contra reservas activas
        }

        const resultado = evaluarDisponibilidad({
          inicioReserva,
          finReserva,
          reservasActivas: reservasActivasList,
          datosInhabiles,
          margenAntes,
          margenDespues
        });

        if (resultado.estado === 'disponible') {
          setEstadoDisponibilidad('disponible');
          setHoraPropuesta(null);
          setTipoConflicto(null);
        } else if (resultado.estado === 'conflicto') {
          setHoraPropuesta(resultado.horaPropuesta);
          setEstadoDisponibilidad('conflicto');
          setTipoConflicto(resultado.tipoConflicto);
        } else {
          setEstadoDisponibilidad('error');
          setHoraPropuesta(null);
          setTipoConflicto(resultado.tipoConflicto || null);
        }
      } catch (error) {
        console.error("[FaseFechaHora] Error general:", error);
        setEstadoDisponibilidad('error');
      }
    };

    verificarDisponibilidad();
  }, [fechaHora, margenDespues, margenAntes, configCargada, t, accessToken, margenServicioProgramado]);

  useEffect(() => {
    if (tipoServicio === 'inmediato') {
      const fechaISO = new Date().toISOString();
      console.log("[FaseFechaHora] Servicio inmediato - fecha_hora_inicio:", fechaISO);
      onNext({ fecha_hora_inicio: fechaISO });
    }
    // eslint-disable-next-line
  }, [tipoServicio, onNext]);

  if (tipoServicio === 'inmediato') {
    return null;
  }

  // Calcula el mínimo permitido (ahora + horas configuradas)
  const minDate = new Date(Date.now() + margenServicioProgramado * 60 * 60 * 1000);

  const handleCalendarClick = () => {
    if (inputRef.current) {
      inputRef.current.showPicker
        ? inputRef.current.showPicker()
        : inputRef.current.focus();
    }
  };

  const handleFechaChange = (e) => {
    setFechaHora(e.target.value);
    setEstadoDisponibilidad('pendiente');
    setError('');
  };

  const handleSubmit = () => {
    if (!fechaHora || estadoDisponibilidad !== 'disponible') {
      return;
    }
    const fechaISO = new Date(fechaHora).toISOString();
    console.log("[FaseFechaHora] Fecha seleccionada (datetime-local):", fechaHora);
    console.log("[FaseFechaHora] Fecha convertida a ISO:", fechaISO);
    onNext({ fecha_hora_inicio: fechaISO, disponible: true });
  };

  const aceptarPropuesta = () => {
    if (horaPropuesta) {
      const duracion = 2 * 60 * 60 * 1000; // 2 horas por defecto
      const nuevaHoraFin = new Date(horaPropuesta.getTime() + duracion);
      
      console.log("[FaseFechaHora] Aceptando propuesta de horario:", {
        nueva_inicio: horaPropuesta.toISOString(),
        nueva_fin: nuevaHoraFin.toISOString()
      });
      
      // Avanzar directamente al siguiente paso con la nueva fecha
      onNext({ 
        fecha_hora_inicio: horaPropuesta.toISOString(),
        disponible: true 
      });
    }
  };

  const rechazarPropuesta = () => {
    window.location.href = '/';
  };

  return (
    <Stack spacing={6} align="center">
      <Heading size="md">{t('fecha_hora_titulo')}</Heading>
      
      <Box w="100%" maxW="300px" textAlign="center">
        <InputGroup>
          <InputLeftElement width="3rem">
            <IconButton
              aria-label={t('abrir_calendario')}
              icon={<CalendarSvg />}
              variant="ghost"
              onClick={handleCalendarClick}
              tabIndex={-1}
            />
          </InputLeftElement>
          <Input
            ref={inputRef}
            type="datetime-local"
            value={fechaHora}
            onChange={handleFechaChange}
            min={minDate.toISOString().slice(0, 16)}
            max={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)}
            bg="black"
            color="white"
            border="1px solid #444"
            borderRadius="6px"
            padding="10px"
            fontSize="1.1em"
            pl="3rem"
          />
        </InputGroup>
        {error && (
          <Text color="red.300" fontSize="sm" mt={2}>
            {error}
          </Text>
        )}
      </Box>

      {/* Estado de verificación */}
      {estadoDisponibilidad === 'verificando' && (
        <VStack spacing={3}>
          <Spinner size="lg" color="teal.500" />
          <Text className="verification-text">{t('disponibilidad_verificando')}</Text>
        </VStack>
      )}

      {/* Disponibilidad confirmada */}
      {estadoDisponibilidad === 'disponible' && (
        <Text color="green.500" fontSize="lg" fontWeight="bold">
          ✓ {t('disponibilidad_ok')}
        </Text>
      )}

      {/* Conflicto con propuesta alternativa */}
      {estadoDisponibilidad === 'conflicto' && horaPropuesta && (
        <VStack spacing={4} w="100%" maxW="500px">
          <Heading size="sm" color="orange.500">
            {t('disponibilidad_conflicto_titulo')}
          </Heading>
          
          <Box bg="orange.50" p={4} borderRadius="md" borderWidth="1px" borderColor="orange.200" w="100%">
            <VStack spacing={2} align="start">
              <Text className="conflict-message-title">
                {t('disponibilidad_conflicto_mensaje')}
              </Text>
              <Text className="conflict-message-text">
                • {tipoConflicto === 'inhabil' 
                    ? t('disponibilidad_motivo_inhabil') 
                    : t('disponibilidad_motivo_reserva')}
              </Text>
            </VStack>
          </Box>

          <Box bg="green.50" p={4} borderRadius="md" borderWidth="1px" borderColor="green.200" w="100%">
            <VStack spacing={2} align="start">
              <Text className="proposal-title">
                {t('disponibilidad_propuesta_titulo')}
              </Text>
              <Text className="proposal-datetime">
                {horaPropuesta.toLocaleString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Text>
            </VStack>
          </Box>

          <HStack spacing={4} pt={2}>
            <Button colorScheme="red" variant="outline" onClick={rechazarPropuesta}>
              {t('boton_cancelar')}
            </Button>
            <Button colorScheme="green" onClick={aceptarPropuesta}>
              {t('disponibilidad_aceptar_propuesta')}
            </Button>
          </HStack>
        </VStack>
      )}

      {/* Error sin alternativa */}
      {estadoDisponibilidad === 'error' && (
        <VStack spacing={3}>
          <Text className="error-message-title">
            {t('disponibilidad_error')}
          </Text>
          <Text className="error-message-text">
            {t('disponibilidad_error_mensaje')}
          </Text>
          {tipoConflicto && (
            <Text className="conflict-message-text">
              {tipoConflicto === 'inhabil' 
                ? t('disponibilidad_motivo_inhabil') 
                : t('disponibilidad_motivo_reserva')}
            </Text>
          )}
        </VStack>
      )}

      {/* Botones de navegación */}
      {estadoDisponibilidad !== 'conflicto' && (
        <Stack direction="row" spacing={4}>
          <Button onClick={onBack}>{t('boton_atras')}</Button>
          <Button 
            colorScheme="teal" 
            onClick={handleSubmit} 
            isDisabled={!fechaHora || estadoDisponibilidad !== 'disponible'}
          >
            {t('boton_siguiente')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default FaseFechaHora;